import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { TABLE_ROW_H, type ObjectData, type SceneStore } from '@os/doc';
import { CellEditor } from '../table/CellEditor';
import { onCellEditRequest } from '../table/cell-edit';
import { worldPerScreenPx } from './zoom';

type Table = Extract<ObjectData, { type: 'table' }>;

/**
 * A collaborative table: fixed 40 px rows, text cut with an ellipsis; the canvas controller asks to edit a cell
 * (double-click, Enter) through `requestCellEdit` — pointer capture retargets native double-clicks — Tab/Enter move on,
 * a column border drags its width (one write at the end). Controls are ignored by the canvas (`data-canvas-ignore`).
 */
export function TableObject({ o, store }: { o: Table; store: SceneStore }) {
  const { t } = useTranslation();
  const readOnly = useSyncExternalStore(
    (cb) => store.subscribeReadOnly(cb),
    () => store.readOnly,
  );
  const [editing, setEditing] = useState<{ row: number; col: number } | null>(null);
  const [drag, setDrag] = useState<{ col: string; w: number } | null>(null);
  const start = useRef<{ x: number; w: number; scale: number } | null>(null);
  useEffect(
    () =>
      onCellEditRequest(store, o.id, (rowId, colId) => {
        const cur = store.getObject(o.id);
        if (store.readOnly || cur?.type !== 'table') return;
        const row = cur.rows.findIndex((r) => r.id === rowId);
        const col = cur.cols.findIndex((c) => c.id === colId);
        if (row >= 0 && col >= 0) setEditing({ row, col });
      }),
    [store, o.id],
  );
  const widths = o.cols.map((c) => (drag?.col === c.id ? drag.w : c.w));
  const move = (row: number, col: number) => {
    if (col >= o.cols.length) [row, col] = [row + 1, 0];
    if (col < 0) [row, col] = [row - 1, o.cols.length - 1];
    setEditing(row >= 0 && row < o.rows.length ? { row, col } : null);
  };
  return (
    <div className="relative h-full w-full bg-white font-sans text-sm text-[#141414]">
      <div className="grid h-full w-full" style={{ gridTemplateColumns: widths.map((w) => `${w}px`).join(' '), gridAutoRows: `${TABLE_ROW_H}px` }}>
        {o.rows.map((r, ri) =>
          o.cols.map((c, ci) => (
            <div
              key={`${r.id}:${c.id}`}
              data-cell={`${r.id}:${c.id}`}
              className={`relative min-w-0 border-b border-r border-[#d4d4d8] px-2 leading-[40px] ${ri === 0 ? 'border-t font-semibold' : ''} ${ci === 0 ? 'border-l' : ''}`}
            >
              <span className="block truncate">{r.cells[c.id] ?? ''}</span>
              {editing?.row === ri && editing.col === ci && (
                <CellEditor
                  value={r.cells[c.id] ?? ''}
                  onCommit={(text, next) => {
                    if (text !== (r.cells[c.id] ?? '')) store.commands.tableSetCell(o.id, r.id, c.id, text);
                    if (next === 'none') setEditing(null);
                    else move(next === 'down' ? ri + 1 : ri, next === 'right' ? ci + 1 : next === 'left' ? ci - 1 : ci);
                  }}
                  onCancel={() => setEditing(null)}
                />
              )}
            </div>
          )),
        )}
      </div>
      {!readOnly &&
        o.cols.map((c, ci) => (
          <div
            key={c.id}
            data-col-resize
            data-canvas-ignore
            className="absolute top-0 h-full w-2 -translate-x-1 cursor-col-resize"
            style={{ left: widths.slice(0, ci + 1).reduce((s, w) => s + w, 0) }}
            onPointerDown={(e) => {
              e.currentTarget.setPointerCapture?.(e.pointerId);
              start.current = { x: e.clientX, w: c.w, scale: worldPerScreenPx(e.currentTarget) };
              setDrag({ col: c.id, w: c.w });
            }}
            onPointerMove={(e) => {
              const s = start.current;
              if (s) setDrag({ col: c.id, w: Math.min(800, Math.max(40, Math.round(s.w + (e.clientX - s.x) * s.scale))) });
            }}
            onPointerUp={(e) => {
              const s = start.current;
              start.current = null;
              setDrag(null);
              if (s) store.commands.tableResizeCol(o.id, c.id, s.w + (e.clientX - s.x) * s.scale);
            }}
          />
        ))}
      {!readOnly && (
        <>
          <button type="button" data-add="col" data-canvas-ignore aria-label={t('table.addCol')} onClick={() => store.commands.tableInsertCol(o.id, o.cols.length)} className="absolute -right-7 top-0 flex h-6 w-6 items-center justify-center rounded-full border border-[#d4d4d8] bg-white text-[#52525b] hover:text-[#141414]">
            +
          </button>
          <button type="button" data-add="row" data-canvas-ignore aria-label={t('table.addRow')} onClick={() => store.commands.tableInsertRow(o.id, o.rows.length)} className="absolute -bottom-7 left-0 flex h-6 w-6 items-center justify-center rounded-full border border-[#d4d4d8] bg-white text-[#52525b] hover:text-[#141414]">
            +
          </button>
        </>
      )}
    </div>
  );
}
